const ICON_LINK_SELECTOR = "link[rel='icon']";
const MANAGED_ICON_ATTRIBUTE = "data-favicon-source";
const MANAGED_ICON_VALUE = "runtime";

// NOTE: The implicit default a browser uses when no <link rel="icon"> is
// declared, and the one `index.html` declares for the NodeShift flavor.
const IMPLICIT_DEFAULT_FAVICON_HREF = "/favicon.ico";

interface IDeclaredFaviconLink {
  href: string;
  type: string | null;
  media: string | null;
}

interface IGetOrgFaviconUrlProps {
  cacheBuster?: number | null;
  // NOTE: The project whose branding answer said there is a favicon. Only a
  // cache key — the endpoint resolves the organization from its own cookie —
  // but without it every organization shares one URL.
  projectUuid?: string | null;
}

// NOTE: NSAI-6321 — the tab icon has two writers that know nothing about each
// other: the organization's custom favicon (resolved once the branding lookup
// answers, and live on every screen including pre-login) and the unread-count
// badge (`use-favicon-badge`, mounted only in the authenticated layout). Before
// this module each of them removed and re-added `link[rel='icon']` on its own,
// so whichever effect ran last won — and React runs a child's effect before its
// parent's, which meant the branding write would land after the badge write and
// silently erase the count.
//
// So neither writes the DOM any more. Each sets its own slot here, and this
// module renders the one icon both slots imply: the badge when there is one
// (it is already painted ON TOP of the organization's own favicon), otherwise
// the custom favicon, otherwise whatever `index.html` declared. Order stops
// mattering, because every setter re-renders from the full state.
let declaredFaviconLinks: IDeclaredFaviconLink[] | null = null;
let customFaviconHref: string | null = null;
let badgeFaviconHref: string | null = null;
// NOTE: Whether this module has replaced what `index.html` declared. Both slots
// start empty and the theme provider clears its slot once on mount, so without
// this every boot would remove and re-append the default link — costing a
// re-fetch of the icon, and a blink of an empty tab, to arrive back where the
// document already was.
let hasReplacedDeclaredLinks = false;

// NOTE: Captured once, lazily, BEFORE anything here has written to the head —
// after the first render the only icon links left are this module's own, so a
// later read would return a runtime href as if it were the flavor default.
// `link.href` rather than `getAttribute` on purpose: the absolute URL survives
// being re-added from a different document base.
const getDeclaredFaviconLinks = (): IDeclaredFaviconLink[] => {
  if (declaredFaviconLinks) {
    return declaredFaviconLinks;
  }

  const links = [
    ...document.querySelectorAll<HTMLLinkElement>(ICON_LINK_SELECTOR),
  ].map((link) => ({
    href: link.href,
    type: link.getAttribute("type"),
    media: link.getAttribute("media"),
  }));

  declaredFaviconLinks = links;

  return links;
};

const removeAllFaviconLinks = (): void => {
  for (const link of document.querySelectorAll(ICON_LINK_SELECTOR)) {
    link.remove();
  }
};

// NOTE: The link is replaced rather than mutated. Mutating `href` works on the
// way IN (default → data URL) but Chromium often fails to refresh the tab icon
// on the way OUT (data URL → a real file): the icon stays stuck on the last
// data URL even though the attribute changed. Removing and re-appending forces
// the browser to re-evaluate.
const renderFaviconLinks = (): void => {
  if (typeof document === "undefined") {
    return;
  }

  // NOTE: Read the declared links before the first removal, or they are gone.
  const declaredLinks = getDeclaredFaviconLinks();
  const runtimeHref = badgeFaviconHref ?? customFaviconHref;

  if (!runtimeHref && !hasReplacedDeclaredLinks) {
    return;
  }

  removeAllFaviconLinks();
  hasReplacedDeclaredLinks = !!runtimeHref;

  if (runtimeHref) {
    const link = document.createElement("link");
    link.rel = "icon";
    link.href = runtimeHref;
    link.setAttribute(MANAGED_ICON_ATTRIBUTE, MANAGED_ICON_VALUE);
    document.head.appendChild(link);
    return;
  }

  // NOTE: Restoring every declared link, not just the first: the Oplot flavor
  // ships two, split by `prefers-color-scheme`, and re-adding one of them would
  // leave that flavor stuck on a single-scheme icon after the first reset.
  for (const declaredLink of declaredLinks) {
    const link = document.createElement("link");
    link.rel = "icon";
    link.href = declaredLink.href;

    if (declaredLink.type) {
      link.type = declaredLink.type;
    }

    if (declaredLink.media) {
      link.media = declaredLink.media;
    }

    document.head.appendChild(link);
  }
};

// NOTE: What `index.html` declared — the flavor's own icon, and the image the
// unread badge falls back to when the organization has no favicon of its own.
//
// Deliberately NOT "the current brand icon": an earlier version of this also
// folded in `customFaviconHref`, and a caller reading that got a stale answer.
// The custom href is owned by the theme provider, whose effect runs AFTER a
// descendant's (React fires a child's passive effects first), so a hook reading
// this module during the same commit as an organization switch saw the previous
// organization's icon — and because the badge slot outranks the custom one in
// `renderFaviconLinks`, the provider's later write could not correct the tab.
// Anything that needs the custom href reads it from the theme context, where it
// is a rendered value rather than shared mutable state.
export const getDeclaredFaviconHref = (): string => {
  const [declaredLink] = getDeclaredFaviconLinks();

  return declaredLink?.href ?? IMPLICIT_DEFAULT_FAVICON_HREF;
};

export const setCustomFaviconHref = (href: string | null): void => {
  customFaviconHref = href;
  renderFaviconLinks();
};

export const setBadgeFaviconHref = (href: string | null): void => {
  badgeFaviconHref = href;
  renderFaviconLinks();
};

// NOTE: The cache buster is the same stamp the logos use — a saved or reset
// favicon has to show up in the tab without a reload, and the URL is otherwise
// constant, so the browser would keep serving the cached image.
//
// NOTE: NSAI-6475 — the project scopes the URL to one organization. Branding
// reads no longer bump the cache buster (that bypassed the endpoint's HTTP
// cache on every boot), so a switch between two organizations that both have a
// favicon left the URL unchanged and the previous organization's icon on
// screen. A per-project URL changes exactly on that switch and stays stable
// across reloads.
export const getOrgFaviconUrl = ({
  cacheBuster,
  projectUuid,
}: IGetOrgFaviconUrlProps = {}): string => {
  const searchParams = new URLSearchParams();
  if (projectUuid) {
    searchParams.set("project", projectUuid);
  }
  if (cacheBuster) {
    searchParams.set("cacheBuster", String(cacheBuster));
  }
  const query = searchParams.toString();

  return query ? `/api/theme/favicon?${query}` : "/api/theme/favicon";
};
